/*
 * Copyright (c) 2016-present, Parse, LLC
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import DashboardView from 'dashboard/DashboardView.react';
import Field from 'components/Field/Field.react';
import Fieldset from 'components/Fieldset/Fieldset.react';
import FlowView from 'components/FlowView/FlowView.react';
import FormTable from 'components/FormTable/FormTable.react';
import Label from 'components/Label/Label.react';
import React from 'react';
import renderFlowFooterChanges from 'lib/renderFlowFooterChanges';
import styles from 'dashboard/Settings/Settings.scss';
import TextInput from 'components/TextInput/TextInput.react';
import Toggle from 'components/Toggle/Toggle.react';
import Toolbar from 'components/Toolbar/Toolbar.react';
import unique from 'lib/unique';
import { getParseServerVersion } from 'lib/helpers';
import semver from 'semver';

export default class UsersSettings extends DashboardView {
  constructor() {
    super();
    this.section = 'App Settings';
    this.subsection = 'Users';
  }

  renderForm({
    fields, setField,
  }) {
    const userSessionsFields =
      (<Fieldset
        legend="User Sessions"
        descriptionWithHTML={'This feature allows for better security and management <br/>of sessions for users.'}>
        <div className={styles.settingsBox}>
          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Expire inactive sessions"
              descriptionWithHTML="We’ll automatically delete revocable sessions that have not made an API request in the last year." />}
            input={<Toggle
              onChange={setField.bind(this, 'expire_revocable_session')}
              value={fields.expire_revocable_session} />} />

          <div className={styles.hr} />

          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Revoke session on password change"
              descriptionWithHTML={'When a user changes or resets their password, we’ll <br/>automatically delete all Session objects associated with this user.'} />}
            input={<Toggle
              onChange={setField.bind(this, 'revoke_on_password_reset')}
              value={fields.revoke_on_password_reset} />} />
        </div>
      </Fieldset>);

    const userAuthenticationFields =
      (<Fieldset
        legend="User Authentication"
        description="You can enable and disable various authentication types for your application and provide additional settings for login methods, which will limit and help secure your application.">
        <div className={styles.settingsBox}>
          {
            // <Field
            //   className="oneliner"
            //   label={<Label
            //     className="oneliner"
            //     text="Enable new methods by default"
            //     description="If we add new authentication methods, they will be allowed by default." />}
            //   input={<Toggle
            //     onChange={setField.bind(this, 'enableNewMethodsByDefault')}
            //     value={!!fields.enableNewMethodsByDefault} />} />

            // <div className={styles.hr} />
          }

          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Allow username and password-based authentication" />}
            input={<Toggle
              onChange={setField.bind(this, 'allowUsernameAndPassword')}
              value={fields.allowUsernameAndPassword} />} />

          <div className={styles.hr} />

          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Allow anonymous users" />}
            input={<Toggle
              onChange={setField.bind(this, 'allowAnonymousUsers')}
              value={fields.allowAnonymousUsers} />} />
        </div>
      </Fieldset>);


    const parseServerVersion = getParseServerVersion(this.context.currentApp.parseServerVersion);

    const socialLoginFields =
      (<Fieldset
        legend="Social Login"
        description="Enable and secure your app’s third-party login methods.">
        <div className={styles.settingsBox}>

          {
            (parseServerVersion && semver.gte(parseServerVersion, "3.0.0")) && (
              <>
                <Field
                  className="oneliner"
                  label={<Label
                    className="oneliner"
                    text="Allow Sign in with Apple" />}
                  input={<Toggle
                    onChange={setField.bind(this, 'allowAppleSignIn')}
                    value={fields.allowAppleSignIn} />}
                />

                {
                  (fields.allowAppleSignIn && !fields.appleClientID) && (
                    <div>
                      <Field
                        label={<Label text="Bundle ID" />}
                        input={
                          <TextInput
                            key="appleBundleId"
                            value={fields.tmp_appleClientID}
                            placeholder="Bundle ID"
                            onChange={setField.bind(this, 'tmp_appleClientID')} />
                        } />
                    </div>
                  )
                }

                {
                  (fields.allowAppleSignIn && fields.appleClientID) && (
                    <div>
                      <Field
                        input={<FormTable
                          keyWidth="75px"
                          items={[fields.appleClientID].map(() => ({
                            title: 'Active Apple Sign In',
                            color: 'green',
                            onDelete: () => {
                              setField('appleClientID', '');
                              setField('allowAppleSignIn', false);
                            },
                            notes: [
                              {
                                key: 'Bundle ID',
                                value: fields.appleClientID,
                              },
                            ],
                          }))} />} />
                    </div>
                  )
                }

                <div className={styles.hr} />

              </>
            )
          }


          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Allow Facebook authentication" />}
            input={<Toggle
              onChange={setField.bind(this, 'allowFacebookAuthentication')}
              value={fields.allowFacebookAuthentication} />} />

          {(fields.allowFacebookAuthentication && !(fields.facebookAppIDList && fields.facebookAppIDList.length > 0) && !(fields.facebookAppSecretList && fields.facebookAppSecretList.length > 0)) ?
            <div>
              <Field
                label={<Label text="Facebook App ID" />}
                input={
                  <TextInput
                    key="id"
                    value={fields.facebookAppID}
                    placeholder="App ID"
                    onChange={setField.bind(this, 'facebookAppID')} />
                } />

              <Field
                label={<Label text="Facebook App Secret" />}
                input={
                  <TextInput
                    key="secret"
                    value={fields.facebookAppSecret}
                    placeholder="App secret"
                    onChange={setField.bind(this, 'facebookAppSecret')} />
                } />
            </div> : null}

          {(fields.allowFacebookAuthentication && (fields.facebookAppIDList && fields.facebookAppIDList.length > 0) && (fields.facebookAppSecretList && fields.facebookAppSecretList.length > 0)) ?
            <div>
              <Field
                input={<FormTable
                  keyWidth="75px"
                  items={fields.facebookAppIDList.map((id, index) => ({
                    title: 'Active Facebook App',
                    color: 'green',
                    onDelete: () => {
                      setField('facebookAppIDList', []);
                      setField('facebookAppSecretList', []);
                      setField('allowFacebookAuthentication', false);
                    },
                    notes: [
                      {
                        key: 'ID',
                        value: fields.facebookAppIDList[0],
                      },
                      {
                        key: 'Secret',
                        value: fields.facebookAppSecretList[0],
                      },
                    ],
                  }))} />} />
            </div> : null}

          <div className={styles.hr} />

          <Field
            className="oneliner"
            label={<Label
              className="oneliner"
              text="Allow Twitter authentication" />}
            input={<Toggle
              onChange={setField.bind(this, 'allowTwitterAuthentication')}
              value={fields.allowTwitterAuthentication} />} />
          {(fields.allowTwitterAuthentication && !(fields.twitterConsumerKeysList && fields.twitterConsumerKeysList.length > 0) && !(fields.twitterConsumerSecretList && fields.twitterConsumerSecretList.length > 0)) ?
            <div>
              <Field
                label={<Label text="Twitter Consumer Key" />}
                input={
                  <TextInput
                    key="id"
                    value={fields.twitterConsumerKey}
                    placeholder="Key"
                    onChange={setField.bind(this, 'twitterConsumerKey')} />
                } />

              <Field
                label={<Label text="Twitter Consumer Secret" />}
                input={
                  <TextInput
                    key="secret"
                    value={fields.twitterConsumerSecret}
                    placeholder="Secret"
                    onChange={setField.bind(this, 'twitterConsumerSecret')} />
                } />
            </div> : null}

          {(fields.allowTwitterAuthentication && (fields.twitterConsumerKeysList && fields.twitterConsumerKeysList.length > 0) && (fields.twitterConsumerSecretList && fields.twitterConsumerSecretList.length > 0)) ?
            <div>
              <Field
                input={<FormTable
                  keyWidth="75px"
                  items={fields.twitterConsumerKeysList.map((id, index) => ({
                    title: 'Active Twitter App',
                    color: 'green',
                    onDelete: () => {
                      setField('twitterConsumerKeysList', []);
                      setField('twitterConsumerSecretList', []);
                      setField('allowTwitterAuthentication', false);
                    },
                    notes: [
                      {
                        key: 'ID',
                        value: fields.twitterConsumerKeysList[0],
                      },
                      {
                        key: 'Secret',
                        value: fields.twitterConsumerSecretList[0],
                      },
                    ],
                  }))} />} />
            </div> : null}
        </div>
      </Fieldset >);

    return (
      <div className={[styles.settings_page, 'ShowFooter'].join(' ')}>
        <Toolbar section={this.section} subsection={this.subsection} />
        <div className={styles.holder}>
          {userSessionsFields}
          {userAuthenticationFields}
          {socialLoginFields}
        </div>
      </div>
    );
  }

  renderContent() {
    if (!this.props.initialFields) {
      return <Toolbar section={this.section} subsection={this.subsection} />;
    }

    const initialFields = {
      require_revocable_session: this.props.initialFields.require_revocable_session,
      expire_revocable_session: this.props.initialFields.expire_revocable_session,
      revoke_on_password_reset: this.props.initialFields.revoke_on_password_reset,

      enableNewMethodsByDefault: this.props.initialFields.auth_options_attributes._enable_by_default,
      allowUsernameAndPassword: !!this.props.initialFields.auth_options_attributes.username,
      allowAnonymousUsers: !!this.props.initialFields.auth_options_attributes.anonymous,
      allowCustomAuthentication: (this.props.initialFields.auth_options_attributes.custom || { enabled: false }).enabled,

      allowAppleSignIn: (this.props.initialFields.auth_options_attributes.apple || { enabled: false }).enabled,
      appleClientID: (this.props.initialFields.auth_options_attributes.apple || {}).client_id,

      tmp_appleClientID: '',

      allowFacebookAuthentication: (this.props.initialFields.auth_options_attributes.facebook || { enabled: false }).enabled,
      facebookAppIDList: (this.props.initialFields.auth_options_attributes.facebook || { app_ids: [] }).app_ids,
      facebookAppSecretList: (this.props.initialFields.auth_options_attributes.facebook || { app_secrets: [] }).app_secrets,

      facebookAppID: '',
      facebookAppSecret: '',

      allowTwitterAuthentication: (this.props.initialFields.auth_options_attributes.twitter || { enabled: false }).enabled,
      twitterConsumerKeysList: (this.props.initialFields.auth_options_attributes.twitter || { consumer_keys: [] }).consumer_keys,
      twitterConsumerSecretList: (this.props.initialFields.auth_options_attributes.twitter || { consumer_secrets: [] }).consumer_secrets,

      twitterConsumerKey: '',
      twitterConsumerSecret: '',
    };

    return (<FlowView
      initialFields={initialFields}
      renderForm={this.renderForm.bind(this)}
      footerContents={({ changes }) => renderFlowFooterChanges(changes, initialFields, userFieldOptions)}
      onSubmit={({ changes, fields, setField }) => {
        const __changes = JSON.parse(JSON.stringify(changes));

        if (__changes.auth_options_attributes) {
          __changes.auth_options_attributes = {};

          const auth_options_attributes = [
            ['username', 'allowUsernameAndPassword'],
            ['anonymous', 'allowAnonymousUsers'],
            ['custom', 'allowCustomAuthentication'],
            ['apple', 'allowAppleSignIn'],
            ['facebook', 'allowFacebookAuthentication'],
            ['twitter', 'allowTwitterAuthentication'],
          ];

          for (const [key, val] of auth_options_attributes) {
            if (__changes[val] !== undefined) {
              __changes.auth_options_attributes[key] = __changes[val];
              setField(val, __changes[val]);

              delete __changes[val];
            }
          }
        }

        if (__changes.appleClientID) {
          __changes.allowAppleSignIn = true;
        }

        if (__changes.facebookAppSecret && __changes.facebookAppID) {
          __changes.allowFacebookAuthentication = true;
        }

        if (__changes.twitterConsumerKey && __changes.twitterConsumerSecret) {
          __changes.allowTwitterAuthentication = true;
        }

        return this.props.saveChanges(__changes).then(({ successes, failures }) => {
          for (const k in successes) {
            setField(k, successes[k]);
          }

          if (Object.keys(failures).length > 0) {
            return { error: Object.values(failures).join(' ') };
          }
        })
          .catch(({ error, failures = {} }) => {
            return { error: unique(Object.values(failures).concat(error)).join(' ') };
          });
      }}

      // afterSave={({ setField }) => {
      //   setField('customGCMSenderID', '', true);
      //   setField('customGCMAPIKey', '', true);
      // }}

      validate={({ changes, fields }) => {
        if (Object.keys(changes).length === 0) {
          return true
        }

        if (
          (fields.allowAppleSignIn && !fields.appleClientID) &&
          (!changes.tmp_appleClientID || changes.tmp_appleClientID === '')
        ) {
          return 'A Bundle ID is required.';
        }

        if (
          fields.allowFacebookAuthentication &&
          (changes.facebookAppIDList && !changes.facebookAppIDList.length) &&
          (changes.facebookAppSecretList && !changes.facebookAppSecretList.length) &&
          (!changes.facebookAppID && !changes.facebookAppSecret)
        ) {
          return 'A Facebook ID and Secret are both required.';
        }

        if (changes.facebookAppID && !changes.facebookAppSecret) {
          return 'A Facebook ID and Secret are both required.';
        }

        if (
          fields.allowTwitterAuthentication &&
          (changes.twitterConsumerSecretList && !changes.twitterConsumerSecretList.length) &&
          (changes.twitterConsumerKeysList && !changes.twitterConsumerKeysList.length) &&
          (!changes.twitterConsumerKey && !changes.twitterConsumerSecret)
        ) {
          return 'A Twitter Consumer Key and Secret are both required.';
        }

        if (changes.twitterConsumerKey && !changes.twitterConsumerSecret) {
          return 'A Twitter Consumer Key and Secret are both required.';
        }
      }} />);
  }
}

const userFieldOptions = {
  require_revocable_session: {
    friendlyName: 'require revocable sessions',
    type: 'boolean',
  },
  expire_revocable_session: {
    friendlyName: 'expire inactive sessions',
    type: 'boolean',
  },
  revoke_on_password_reset: {
    friendlyName: 'revoke session on password change',
    type: 'boolean',
  },
  enableNewMethodsByDefault: {
    friendlyName: 'new authentication methods by default',
    type: 'boolean',
  },
  allowUsernameAndPassword: {
    friendlyName: 'user name and password login',
    type: 'boolean',
  },
  allowAnonymousUsers: {
    friendlyName: 'annonymous users',
    type: 'boolean',
  },
  allowAppleSignIn: {
    friendlyName: 'Apple Sign In',
    type: 'boolean',
  },
  tmp_appleClientID: {
    friendlyName: 'Bundle ID',
    type: 'addition',
  },
  allowFacebookAuthentication: {
    friendlyName: 'Facebook authentication',
    type: 'boolean',
  },
  facebookAppID: {
    friendlyName: 'Facebook app ID',
    type: 'addition',
  },
  facebookAppSecret: {
    friendlyName: 'Facebook app secret',
    type: 'addition',
  },
  allowTwitterAuthentication: {
    friendlyName: 'Twitter authentication',
    type: 'boolean',
  },
  twitterConsumerKey: {
    friendlyName: 'Twitter Consumer Key',
    type: 'addition',
  },
  twitterConsumerSecret: {
    friendlyName: 'Twitter Consumer Secret',
    type: 'addition',
  },
  allowCustomAuthentication: {
    friendlyName: 'custom authentication',
    type: 'boolean',
  },
};
